import React, { memo, useCallback, useContext, useEffect, useRef } from 'react';
import { Checkbox } from 'carbon-react/lib/components/checkbox';

import { UICarbonNumberField } from '@futrli/ui-components';
import { FormattedDate } from '@futrli/localisation';

import { UserGridSettingsContext } from '../../../../forecast/detail/reports/user-grid-settings/user-grid-settings-context';
import { FreestyleContentValue } from '../freestyle-content-value/freestyle-content-value';
import { FreestyleMethodCellData } from '../types';
import styles from './freestyle-content-cell.scss';
import { MAX_DECIMALS } from '../../types';

interface FreestyleContentCellProps {
  data: FreestyleMethodCellData;
  format?: 'currency' | 'decimal';
  isDisabled?: boolean;
  onSelect?: (data: FreestyleMethodCellData) => void;
  onValueChange?: (value: string, month: FreestyleMethodCellData) => void;
  pastBlankDataValue?: string;
}

export const FreestyleContentCell = memo(
  ({
    data,
    format = 'currency',
    isDisabled = false,
    onSelect,
    onValueChange,
    pastBlankDataValue = '-'
  }: FreestyleContentCellProps) => {
    const { showDecimals } = useContext(UserGridSettingsContext);
    const isMounted = useRef(false);
    const timeout = useRef<number>();

    const handleValueChange = useCallback(
      (value: string) => {
        window.clearInterval(timeout.current);
        timeout.current = window.setTimeout(() => {
          onValueChange?.(value, data);
        }, 100);
      },
      [data, onValueChange]
    );

    // After init, mark component as mounted
    useEffect(() => {
      isMounted.current = true;
    }, []);

    return (
      <div style={{ width: '8.3%' }} className={styles.carbonContainer}>
        <div className={styles.checkboxContainerCarbon}>
          <Checkbox
            id={`select-${data.date}`}
            tabIndex={-1}
            checked={data.selected}
            onChange={() => onSelect?.(data)}
            disabled={isDisabled || data.readOnly}
            mb={0}
          />
          <label className={styles.dateLabel} htmlFor={`select-${data.date}`}>
            <FormattedDate value={data.localDate} month={'short'} year="2-digit" />
          </label>
        </div>
        {data.readOnly ? (
          <div className={styles.formattedAmount}>
            <FreestyleContentValue
              blankDataValue={pastBlankDataValue}
              format={format}
              showDecimals={!!showDecimals}
              value={data.value}
            />
          </div>
        ) : (
          <UICarbonNumberField
            className={data.isInPast ? styles.inPastInputCarbon : undefined}
            autoComplete="off"
            type="decimal"
            value={data.value}
            onChange={handleValueChange}
            placeholder="-"
            disabled={isDisabled}
            maxDecimals={MAX_DECIMALS}
            size="large"
            mb={0}
          />
        )}
      </div>
    );
  }
);
